import { z } from "zod"
import {
	assertSessionVersion,
	clearSession,
	getAccessToken,
	getRefreshToken,
	getSessionVersion,
	notifySessionExpired,
	setTokens,
} from "@/shared/lib/auth"
import { backendBaseUrl } from "@/shared/lib/platform"
import { HttpError, isHttpError } from "../errors"
import { networkAwareFetch } from "./fetch"
import { noteHttpFailure } from "./telemetry"

const REFRESH_TIMEOUT_MS = 15_000
const REFRESH_PATH = "/api/v1/auth/token/refresh"

export async function resolveAccessToken(): Promise<string | undefined> {
	const version = getSessionVersion()
	const token = await getAccessToken()
	assertSessionVersion(version)
	if (!token) return undefined
	if (!isTokenExpiringSoon(token)) return token
	try {
		const tokens = await ensureTokenRefresh()
		return tokens.access_token
	} catch (error) {
		assertSessionVersion(version)
		if (isHttpError(error) && error.status === 401) return undefined
		throw error
	}
}

/**
 * refresh_token для тех запросов, что передают его телом (logout). Догоняет
 * ротацию до чтения хранилища: и уже идущий рефреш, и тот, что клиент запустил
 * бы сам перед отправкой, — иначе тело уходит с уже заменённым токеном.
 */
export async function getFreshRefreshToken(): Promise<string | null> {
	const version = getSessionVersion()
	if (refreshVersion === version) await refreshMutex?.catch(() => undefined)
	assertSessionVersion(version)
	await resolveAccessToken()
	return getRefreshToken()
}

// access_token обязан быть JWT — из него читается exp в isTokenExpiringSoon.
// refresh_token оставляем строкой: его формат наша сторона не разбирает.
const tokenPairSchema = z.object({
	access_token: z.jwt(),
	refresh_token: z.string().min(1),
})

type TokenPair = z.infer<typeof tokenPairSchema>

let refreshMutex: Promise<TokenPair> | null = null
let refreshVersion = -1

// Один общий рефреш на все конкурентные запросы: создаём промис один раз
// и переиспользуем, пока он не завершится.
export function ensureTokenRefresh(): Promise<TokenPair> {
	const version = getSessionVersion()
	if (!refreshMutex || refreshVersion !== version) {
		refreshVersion = version
		const refresh = doTokenRefresh(version).finally(() => {
			if (refreshMutex === refresh) refreshMutex = null
		})
		refreshMutex = refresh
	}
	return refreshMutex
}

// exp через схему, а не `as number`: у чужого payload поле может быть строкой
// или отсутствовать, и `NaN < ...` тихо выдал бы «токен ещё свежий».
const jwtExpSchema = z.object({ exp: z.number() })

function isTokenExpiringSoon(token: string, bufferSeconds = 60): boolean {
	const payload = token.split(".")[1]
	if (!payload) return false
	try {
		const parsed = jwtExpSchema.safeParse(JSON.parse(atob(payload)))
		if (!parsed.success) return false
		return parsed.data.exp * 1000 < Date.now() + bufferSeconds * 1000
	} catch {
		return false
	}
}

// Signals session expiry on every branch that discards the tokens: the app
// still considers the user authenticated and would loop 401 → refresh → 401.
async function endSession(message: string, clear = true): Promise<never> {
	if (clear) await clearSession()
	notifySessionExpired()
	throw new HttpError(message, 401, undefined, {
		path: REFRESH_PATH,
		method: "POST",
	})
}

async function doTokenRefresh(version: number): Promise<TokenPair> {
	const refreshToken = await getRefreshToken()
	assertSessionVersion(version)
	if (!refreshToken) {
		const hasStoredSession = Boolean(await getAccessToken())
		assertSessionVersion(version)
		return endSession("Unauthorized - no refresh token", hasStoredSession)
	}

	const refreshRes = await networkAwareFetch(
		`${backendBaseUrl}${REFRESH_PATH}`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refresh_token: refreshToken }),
		},
		REFRESH_TIMEOUT_MS,
	)

	if (!refreshRes.ok) {
		noteHttpFailure(refreshRes.status, "POST", REFRESH_PATH)

		// 5xx — прилёг бэкенд, refresh-токен тут ни при чём. Чистить его нельзя:
		// временный сбой сервера разлогинивал бы пользователя. Отдаём исходный
		// статус, а не синтетический 401, — иначе retryTransient не повторит.
		if (refreshRes.status >= 500) {
			throw new HttpError(
				"Token refresh failed",
				refreshRes.status,
				undefined,
				{
					path: REFRESH_PATH,
					method: "POST",
				},
			)
		}

		return endSession("Unauthorized - token refresh failed")
	}

	// Без разбора ответа в хранилище уходит undefined, и пользователь попадает
	// в вечный цикл 401 → refresh → 401.
	const body: unknown = await refreshRes.json().catch(() => undefined)
	assertSessionVersion(version)
	const tokens = tokenPairSchema.safeParse(body)
	if (!tokens.success) {
		return endSession("Unauthorized - malformed refresh response")
	}

	await setTokens(tokens.data.access_token, tokens.data.refresh_token, version)
	return tokens.data
}
